import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Header from "./components/Header";
import Footer from "./components/Footer";
import CookieBanner from "./components/CookieBanner";
import ScrollToTop from "./components/ScrollToTop";
import HomePage from "./pages/HomePage";
import ServicesPage from "./pages/ServicesPage";
import GalleryPage from "./pages/GalleryPage";
import AvisPage from "./pages/AvisPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import SecteursPage from "./pages/SecteursPage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import DataDeletionRequest from "./pages/DataDeletionRequest";
import PublicQuoteView from "./pages/PublicQuoteView";
import { GoogleMapsProvider } from "./components/GoogleMapsProvider";

// Universal Authentication
import { UniversalAuthProvider } from "./contexts/UniversalAuthContext";
import UniversalPrivateRoute from "./components/auth/PrivateRoute";
import UniversalLogin from "./pages/auth/UniversalLogin";
import ForgotPasswordUniversal from "./pages/auth/ForgotPasswordUniversal";
import SetPassword from "./pages/auth/SetPassword";
import ForceChangePassword from "./pages/auth/ForceChangePassword";

// Admin Pages
import AdminDashboard from "./pages/admin/AdminDashboard";
import SubmissionDetail from "./pages/admin/SubmissionDetail";
import ERPDashboardNew from "./pages/admin/ERPDashboardNew";
import ERPConfig from "./pages/admin/erp/ERPConfig";
import ERPClients from "./pages/admin/erp/ERPClients";
import ERPServices from "./pages/admin/erp/ERPServices";
import ERPPackages from "./pages/admin/erp/ERPPackages";
import ERPDepartments from "./pages/admin/erp/ERPDepartments";
import ERPInventory from "./pages/admin/erp/ERPInventory";
import ERPPaymentModalities from "./pages/admin/erp/ERPPaymentModalities";
import ERPEmployees from "./pages/admin/erp/ERPEmployees";
import ERPProfile from "./pages/admin/erp/ERPProfile";
import ClientDetail from "./pages/admin/erp/ClientDetail";
import EmployeeDetail from "./pages/admin/erp/EmployeeDetail";
import Leads from "./pages/admin/Leads";
import LeadDetail from "./pages/admin/LeadDetail";

// Production Module (Nouveau Système)
import ProductionRoutes from "./pages/admin/production/ProductionRoutes";
import RouteDetail from "./pages/admin/production/RouteDetail";
import StopDetail from "./pages/admin/production/StopDetail";
import DailyDashboard from "./pages/admin/production/DailyDashboard";
import ProductsCatalog from "./pages/admin/production/ProductsCatalog";

// Production Module (Legacy - À retirer après validation E2E)
import ProductionLayout from "./pages/admin/production/ProductionLayout";
import JobsDispatch from "./pages/admin/production/JobsDispatch";
import JobsList from "./pages/admin/production/JobsList";
import JobDetail from "./pages/admin/production/JobDetail";
import Teams from "./pages/admin/production/Teams";
import Vehicles from "./pages/admin/production/Vehicles";
import CalendarView from "./pages/admin/production/CalendarView";
import ProfitabilityDashboard from "./pages/admin/production/ProfitabilityDashboard";

// Client Portal (DEPRECATED - à migrer vers nouveau système)
import { ClientAuthProvider } from "./contexts/ClientAuthContext";
import ClientQuoteView from "./pages/client/ClientQuoteView";
import ClientConfirmation from "./pages/client/ClientConfirmation";
import ClientDashboard from "./pages/client/ClientDashboard";
import ClientQuotes from "./pages/client/ClientQuotes";
import ClientProperty from "./pages/client/ClientProperty";

function App() {
  return (
    <GoogleMapsProvider>
      <UniversalAuthProvider>
        <ClientAuthProvider>
          <div className="App">
            <BrowserRouter>
              <ScrollToTop />
              <Routes>
                {/* ========================================
                    UNIVERSAL AUTHENTICATION ROUTES
                    ======================================== */}
                <Route path="/auth/login" element={<UniversalLogin />} />
                <Route path="/auth/forgot-password" element={<ForgotPasswordUniversal />} />
                <Route path="/auth/reset-password/:token" element={<SetPassword />} />
                <Route path="/auth/set-password/:token" element={<SetPassword />} />
                <Route path="/auth/force-change-password" element={<ForceChangePassword />} />

                {/* Redirections des anciennes routes */}
                <Route path="/admin" element={<Navigate to="/auth/login" replace />} />
                <Route path="/client/login" element={<Navigate to="/auth/login" replace />} />

                {/* ========================================
                    ADMIN/MANAGER ROUTES (Protected)
                    ======================================== */}
                <Route element={<UniversalPrivateRoute allowedRoles={["super_admin", "admin", "manager"]} />}>
                  <Route path="/admin/dashboard" element={<AdminDashboard />} />
                  <Route path="/admin/submissions/:id" element={<SubmissionDetail />} />
                  <Route path="/admin/erp" element={<ERPDashboardNew />} />
                  <Route path="/admin/leads" element={<Leads />} />
                  <Route path="/admin/leads/:leadId" element={<LeadDetail />} />
                  <Route path="/admin/erp/config" element={<ERPConfig />} />
                  <Route path="/admin/erp/clients" element={<ERPClients />} />
                  <Route path="/admin/erp/client/:clientId" element={<ClientDetail />} />
                  <Route path="/admin/erp/services" element={<ERPServices />} />
                  <Route path="/admin/erp/departments" element={<ERPDepartments />} />
                  <Route path="/admin/erp/inventory" element={<ERPInventory />} />
                  <Route path="/admin/erp/packages" element={<ERPPackages />} />
                  <Route path="/admin/erp/payment-modalities" element={<ERPPaymentModalities />} />
                  <Route path="/admin/erp/employees" element={<ERPEmployees />} />
                  <Route path="/admin/erp/employees/:employeeId" element={<EmployeeDetail />} />
                  <Route path="/admin/erp/profile" element={<ERPProfile />} />
                  <Route path="/admin/erp/clients/:clientId" element={<ClientDetail />} />
                  
                  {/* Production Module (Nouveau Système) */}
                  <Route path="/admin/production/routes" element={<ProductionRoutes />} />
                  <Route path="/admin/production/routes/:routeId" element={<RouteDetail />} />
                  <Route path="/admin/production/stops/:stopId" element={<StopDetail />} />
                  <Route path="/admin/production/dashboard" element={<DailyDashboard />} />
                  <Route path="/admin/production/products" element={<ProductsCatalog />} />
                  
                  {/* Production Module (Legacy - Temporaire) */}
                  <Route path="/admin/production/legacy" element={<ProductionLayout />}>
                    <Route path="dispatch" element={<JobsDispatch />} />
                    <Route path="jobs" element={<JobsList />} />
                    <Route path="jobs/:jobId" element={<JobDetail />} />
                    <Route path="calendar" element={<CalendarView />} />
                    <Route path="teams" element={<Teams />} />
                    <Route path="vehicles" element={<Vehicles />} />
                  </Route>
                  
                  {/* Module Rentabilité */}
                  <Route path="/admin/production/profitability" element={<ProfitabilityDashboard />} />
                </Route>

                {/* ========================================
                    CLIENT PORTAL ROUTES (Protected)
                    TODO: Migrate fully to Universal Auth
                    ======================================== */}
                <Route path="/client/quote/:token" element={<ClientQuoteView />} />
                <Route path="/client/confirmation" element={<ClientConfirmation />} />
                
                {/* Client routes protégées (utilise encore ancien système temporairement) */}
                <Route path="/client/dashboard" element={<ClientDashboard />} />
                <Route path="/client/quotes" element={<ClientQuotes />} />
                <Route path="/client/property" element={<ClientProperty />} />

                {/* ========================================
                    PUBLIC ROUTES
                    ======================================== */}
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/data-deletion" element={<DataDeletionRequest />} />
                <Route path="/devis/:token" element={<PublicQuoteView />} />

                {/* Public website routes with Header/Footer */}
                <Route
                  path="*"
                  element={
                    <>
                      <Header />
                      <Routes>
                        <Route path="/" element={<HomePage />} />
                        <Route path="/services" element={<ServicesPage />} />
                        <Route path="/secteurs" element={<SecteursPage />} />
                        <Route path="/galerie" element={<GalleryPage />} />
                        <Route path="/avis" element={<AvisPage />} />
                        <Route path="/a-propos" element={<AboutPage />} />
                        <Route path="/contact" element={<ContactPage />} />
                      </Routes>
                      <Footer />
                      <CookieBanner />
                    </>
                  }
                />
              </Routes>
            </BrowserRouter>
          </div>
        </ClientAuthProvider>
      </UniversalAuthProvider>
    </GoogleMapsProvider>
  );
}

export default App;
